import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  MoreHorizontal,
  FileStack,
  Hash,
  RotateCcw,
  Pencil,
  Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Props {
  student: any;
  onCreateCombined: (s: any) => Promise<void> | void;
  onChanged: () => void;
}

// Cost component IDs eligible for adjustment
const HOSTEL_COST_TYPE_ID = "776e5802-9e6c-4973-a73e-38cd04fbda9f";

const fmt = (n: number) => new Intl.NumberFormat().format(Math.round(n));

type AdjRow = {
  invoiceId: string;
  label: string;
  billed: number;
  originalDiscount: number;
  finalInput: string;
};

export function InvoiceActionsMenu({ student, onCreateCombined, onChanged }: Props) {
  const invoice = student.schoolFeeInvoice ?? null;
  const hasInvoice = !!invoice;
  const hasControl = !!invoice?.control_number;

  const [busy, setBusy] = useState<null | "create" | "generate" | "revoke">(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Adjustment dialog state
  const [loadingAdj, setLoadingAdj] = useState(false);
  const [rows, setRows] = useState<AdjRow[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editOpen) return;
    let cancelled = false;
    (async () => {
      setLoadingAdj(true);
      try {
        // Fetch latest school_fee invoice (Fee & Admission) + all Hostel invoices
        const [feeRes, hostelRes] = await Promise.all([
          supabase
            .from("invoices")
            .select("id, amount, discount_amount, description, created_at")
            .eq("student_id", student.id)
            .eq("kind", "school_fee")
            .order("created_at", { ascending: false })
            .limit(1),
          supabase
            .from("invoices")
            .select("id, amount, discount_amount, description, created_at")
            .eq("student_id", student.id)
            .eq("cost_type_id", HOSTEL_COST_TYPE_ID)
            .order("created_at", { ascending: true }),
        ]);
        if (cancelled) return;
        const built: AdjRow[] = [];
        const fee = feeRes.data?.[0];
        // Source F&A billed from the fee structure (sum of installments 1-4),
        // not the school_fee invoice amount (which for continuing students is the full annual total).
        const faBilled = Number(student?.fee?.schoolFeeTotal ?? 0);
        const catName: string | null = student?.fee?.categoryName ?? null;
        if (fee) {
          const disc = Number(fee.discount_amount ?? 0);
          built.push({
            invoiceId: fee.id,
            label: catName
              ? `Fee & Admission (Installments 1–4) · ${catName}`
              : "Fee & Admission (Installments 1–4)",
            billed: faBilled,
            originalDiscount: disc,
            finalInput: String(Math.max(faBilled - disc, 0)),
          });
        }
        for (const h of hostelRes.data ?? []) {
          const billed = Number(h.amount ?? 0);
          if (billed <= 0) continue; // skip zero-billed semesters
          const disc = Number(h.discount_amount ?? 0);
          built.push({
            invoiceId: h.id,
            label: h.description || "Hostel",
            billed,
            originalDiscount: disc,
            finalInput: String(Math.max(billed - disc, 0)),
          });
        }
        setRows(built);
      } catch (e: any) {
        toast({ title: "Failed to load invoices", description: e.message, variant: "destructive" });
      } finally {
        if (!cancelled) setLoadingAdj(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editOpen, student.id]);

  const updateFinal = (id: string, v: string) =>
    setRows(prev => prev.map(r => r.invoiceId === id ? { ...r, finalInput: v } : r));

  const handleCreate = async () => {
    setBusy("create");
    try { await onCreateCombined(student); } finally { setBusy(null); }
  };

  const handleGenerate = async () => {
    setBusy("generate");
    try {
      const { data, error } = await supabase.rpc("generate_control_number", { _student_id: student.id });
      if (error) throw error;
      toast({ title: "Control # generated", description: data as string });
      onChanged();
    } catch (e: any) {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const handleRevoke = async () => {
    if (!invoice) return;
    setBusy("revoke");
    try {
      const { data: fresh } = await supabase
        .from("invoices")
        .select("control_number")
        .eq("id", invoice.id)
        .maybeSingle();
      if (!fresh?.control_number) {
        toast({ title: "No control # to revoke", description: "This invoice has no active control number." });
        onChanged();
        return;
      }
      const { error } = await supabase
        .from("invoices")
        .update({ control_number: null })
        .eq("id", invoice.id);
      if (error) throw error;
      toast({ title: "Control # revoked", description: `${fresh.control_number} cleared. You can now generate a fresh one.` });
      onChanged();
    } catch (e: any) {
      toast({ title: "Revoke failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(null);
      setRevokeOpen(false);
    }
  };

  const saveAdjustments = async () => {
    const targets = rows.filter(r => {
      const f = Number(r.finalInput);
      if (Number.isNaN(f) || f < 0 || f > r.billed) return false;
      const newDiscount = r.billed - f;
      return Math.abs(newDiscount - r.originalDiscount) > 0.001;
    });
    if (!targets.length) { toast({ title: "No changes to apply" }); return; }
    setSaving(true);
    let ok = 0, fail = 0;
    for (const r of targets) {
      const newDiscount = r.billed - Number(r.finalInput);
      const { error } = await supabase.from("invoices")
        .update({ discount_amount: newDiscount })
        .eq("id", r.invoiceId);
      if (error) fail++; else ok++;
    }
    setSaving(false);
    toast({
      title: "Adjustments saved",
      description: `Updated ${ok}${fail ? ` · Failed ${fail}` : ""}`,
      variant: fail ? "destructive" : "default",
    });
    setEditOpen(false);
    onChanged();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={!!busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
            <span className="ml-1 hidden sm:inline">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Invoice actions</DropdownMenuLabel>
          <DropdownMenuItem onClick={handleCreate} disabled={!!busy}>
            <FileStack className="h-4 w-4 mr-2" />
            Create invoices
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleGenerate}
            disabled={!hasInvoice || hasControl || !!busy}
          >
            <Hash className="h-4 w-4 mr-2" />
            Generate control #
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setRevokeOpen(true)}
            disabled={!hasInvoice || !!busy}
            className="text-destructive focus:text-destructive"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Revoke control #
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit adjustments
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke control number?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears <strong>{invoice?.control_number}</strong> from the school-fee invoice
              for <strong>{student.first_name} {student.last_name}</strong>. You can then generate
              a fresh control number. Existing payments are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={editOpen} onOpenChange={(v) => { if (!saving) setEditOpen(v); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit adjustments — {student.first_name} {student.last_name}</DialogTitle>
            <DialogDescription>
              Set the Final Amount per cost component (Fee & Admission, Hostel) after scholarship or exemption.
              Discount is calculated automatically as Billed − Final.
            </DialogDescription>
          </DialogHeader>

          {loadingAdj ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading invoices…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No eligible invoices (Fee &amp; Admission or Hostel) found for this student.
            </div>
          ) : (
            <div className="border rounded-md overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cost component</TableHead>
                    <TableHead className="text-right">Billed</TableHead>
                    <TableHead className="text-right w-40">Final</TableHead>
                    <TableHead className="text-right">Discount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => {
                    const finalNum = r.finalInput === "" ? NaN : Number(r.finalInput);
                    const invalid = Number.isNaN(finalNum) || finalNum < 0 || finalNum > r.billed;
                    const discount = Number.isNaN(finalNum) ? null : r.billed - finalNum;
                    return (
                      <TableRow key={r.invoiceId}>
                        <TableCell>{r.label}</TableCell>
                        <TableCell className="text-right">{fmt(r.billed)}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            max={r.billed}
                            value={r.finalInput}
                            onChange={(e) => updateFinal(r.invoiceId, e.target.value)}
                            className={`h-8 text-right ${invalid ? "border-destructive" : ""}`}
                          />
                        </TableCell>
                        <TableCell className={`text-right ${discount && discount < 0 ? "text-destructive" : ""}`}>
                          {discount == null ? "—" : fmt(discount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveAdjustments} disabled={saving || loadingAdj || rows.length === 0}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
